import React, { FC, useEffect, useState } from 'react'
import { MdCheck } from 'react-icons/md'
import { useTranslation } from 'react-i18next'

interface Props {
  children: React.ReactNode
  type?: 'button' | 'submit' | 'reset'
  onClick?: () => void
  className?: string
  size?: 'sm'
  disabled?: boolean
  loading?: boolean
  theme?: 'white' | 'cyan' | 'blue' | 'dark-blue' | 'red' | 'gray' | 'ghost'
  tooltip?: string
  showLoadingFeedback?: boolean
}
const Button: FC<Props> = ({
  children,
  type = 'button',
  onClick = () => {},
  className = '',
  size,
  loading = false,
  disabled = false,
  theme = 'cyan',
  tooltip,
  showLoadingFeedback = false,
}) => {
  const { t } = useTranslation('common')
  const [wasLoading, setWasLoading] = useState(false)
  const [showLoadingDoneMessage, setShowLoadingDoneMessage] = useState(false)

  let sizeClasses = 'py-2 px-4'
  if (size === 'sm') {
    sizeClasses = 'py-1 px-2'
  } else if (theme === 'ghost') {
    sizeClasses = ''
  }

  let bgColor = 'bg-theme-cyan'
  let bgHoverColor = 'hover:bg-theme-blue'
  let color = 'text-white'
  let hoverColor = 'hover:text-black'
  let hoverUnderline = ''

  switch (theme) {
    case 'blue':
      bgColor = 'bg-theme-blue'
      bgHoverColor = 'hover:bg-theme-dark-blue'
      hoverColor = 'hover:text-white'
      break
    case 'cyan':
      color = 'text-black'
      hoverColor = 'hover:text-white'
      break
    case 'gray':
      bgColor = 'bg-gray-300'
      bgHoverColor = 'hover:bg-gray-400'
      color = 'text-black'
      break
    case 'red':
      bgColor = 'bg-red-500'
      bgHoverColor = 'hover:bg-red-700'
      color = 'text-white'
      hoverColor = 'hover:text-white'
      break
    case 'white':
      bgColor = 'bg-gray-100'
      bgHoverColor = 'hover:bg-gray-300'
      color = 'text-black'
      hoverColor = 'hover:text-black'
      break
    case 'ghost':
      bgColor = 'bg-transparent'
      bgHoverColor = 'bg-transparent'
      color = 'black'
      hoverColor = 'black'
      hoverUnderline = 'underline'
      break
  }

  useEffect(() => {
    if (loading) {
      setWasLoading(true)
    }
    if (!loading && wasLoading) {
      setShowLoadingDoneMessage(true)

      setTimeout(() => {
        setShowLoadingDoneMessage(false)
      }, 2000)
    }
  }, [loading])

  return (
    <div className='flex'>
      <button
        title={tooltip}
        type={type}
        onClick={onClick}
        className={`${className.indexOf('absolute') === -1 ? 'relative' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${sizeClasses} ${bgColor} ${bgHoverColor} ${color} ${hoverColor} hover:${hoverUnderline} transition ease-in duration-200 text-center text-base font-semibold focus:outline-none ${className} rounded-md`}
        disabled={disabled || loading}
      >
        {loading && (
          <div
            role='status'
            className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
          >
            <svg
              aria-hidden='true'
              className='inline w-4 h-4 text-gray-200 animate-spin [animation-direction:reverse] dark:text-gray-600 fill-gray-600 dark:fill-gray-300'
              viewBox='0 0 100 101'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                fill='currentColor'
              />
              <path
                d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                fill='currentFill'
              />
            </svg>
            <span className='sr-only'>{t('common:button.loading')}</span>
          </div>
        )}

        <div className={`${loading ? 'opacity-0' : ''} ${className.indexOf('w-full') > -1 ? 'flex justify-center' : ''}`}>{children}</div>
      </button>
      {showLoadingFeedback && showLoadingDoneMessage && (
        <div className='flex items-center'>
          <MdCheck className='ml-2 mr-1' />
          {t('common:button.saved')}
        </div>
      )}
    </div>
  )
}

export default Button
